import { AnimatePresence } from 'motion/react';
import { FC, PropsWithChildren, Suspense, useMemo } from 'react';
import { useMedia, useWindowSize } from 'react-use';

import { BUTTON_TYPES, media } from '@savgroup-front-common/constants';

import useClientWidth from '../../hooks/useClientWidth';
import { BaseLoader } from '../../molecules/BaseLoader';
import { ExternalPortal } from '../../protons/ExternalPortal/ExternalPortal';
import { CrossIcon } from '../../protons/icons';

import messages from './messages';
import { DIRECTION, DRAWER_SIZE } from './SwipeableDrawer.constants';
import { SwipeableDrawerContext } from './SwipeableDrawer.context';
import { useSwipeableDrawer } from './SwipeableDrawer.hooks';
import {
  $Backdrop,
  $CloseButton,
  $DragHandle,
  $Sheet,
} from './SwipeableDrawer.styles';

export { DIRECTION, DRAWER_SIZE };

interface SwipeableDrawerProps {
  isOpen?: boolean;
  hasBackdrop?: boolean;
  direction?: DIRECTION;
  size?: DRAWER_SIZE;
  onClose?: () => void;
  hasPadding?: boolean;
  hasScroll?: boolean;
  hasBackdropScroll?: boolean;
  dataTestId?: string;
  hasCloseButton?: boolean;
  hasCloseOnBackdrop?: boolean;
  shouldUsePortal?: boolean;
  hasElasticClose?: boolean;
  className?: string;
}

const SwipeableDrawer: FC<PropsWithChildren<SwipeableDrawerProps>> = ({
  children,
  isOpen,
  direction = DIRECTION.LEFT,
  size = DRAWER_SIZE.LARGE,
  hasBackdrop = true,
  onClose,
  hasPadding = true,
  hasScroll = true,
  hasBackdropScroll = true,
  dataTestId,
  hasCloseButton = true,
  hasCloseOnBackdrop = false,
  shouldUsePortal = true,
  hasElasticClose = true,
  className,
}) => {
  const isMobileView = useMedia(media.maxWidth.md);
  const { height: windowHeight } = useWindowSize();
  const windowWidth = useClientWidth();

  const currentSize = isMobileView ? DRAWER_SIZE.FULL_SCREEN : size;
  const currentDirection = isMobileView ? DIRECTION.TOP : direction;

  const { display, height, y, x, dragProps } = useSwipeableDrawer({
    isOpen,
    onClose,
    hasBackdropScroll,
    direction: currentDirection,
    size: currentSize,
    hasElasticClose,
  });

  const contextValue = useMemo(() => ({ hasPadding }), [hasPadding]);

  let style = {
    display,
    right: 0,
    x,
  };

  if (currentDirection === DIRECTION.TOP) {
    style = { display, bottom: `calc(-100vh + ${height - 100}px)`, y } as any;
  }
  if (currentDirection === DIRECTION.RIGHT) {
    style = {
      display,
      left: 0,
      x,
    } as any;
  }

  return (
    <ExternalPortal shouldRenderChildrenWithoutPortal={!shouldUsePortal}>
      <AnimatePresence>
        {isOpen && (
          <div data-testid={dataTestId} className={className}>
            {hasBackdrop && (
              <$Backdrop
                onClick={hasCloseOnBackdrop ? onClose : undefined}
                $opacity={0.5}
              />
            )}
            <$Sheet
              $isMobileView={isMobileView}
              $windowHeight={windowHeight}
              $windowWidth={windowWidth}
              direction={currentDirection}
              size={currentSize}
              style={style}
              $hasPadding={hasPadding}
              $hasScroll={isMobileView ? true : hasScroll}
              {...(dragProps || {})}
              $hasBackdrop={hasBackdrop}
            >
              <SwipeableDrawerContext.Provider value={contextValue}>
                {isOpen && (
                  <>
                    {isMobileView && <$DragHandle />}
                    {hasCloseButton && (
                      <$CloseButton
                        type={BUTTON_TYPES.BUTTON}
                        onClick={onClose}
                        icon={<CrossIcon />}
                        dataTestId="modalClose"
                        primary
                        naked
                        small
                        ariaLabel={messages.closeDrawerButton}
                      />
                    )}
                    <Suspense fallback={<BaseLoader />}>{children}</Suspense>
                  </>
                )}
              </SwipeableDrawerContext.Provider>
            </$Sheet>
          </div>
        )}
      </AnimatePresence>
    </ExternalPortal>
  );
};

export default SwipeableDrawer;
